import { useEffect, useState } from "react";
import { displayToast } from "../../../components/app/AppToast";
import { HapticEffect, SoundEffect } from "../../../interfaces";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventContentLike } from "../../../tools/analytics/domains/content.analytics";
import {
  convertVideoToItemData,
  gaEventVideoLike,
} from "../../../tools/analytics/domains/video.analytics";
import { iosGenerateHaptic, iosPlaySound } from "../../../tools/ios";
import {
  useCurrentUser,
  replaceCurrentUserData,
  type CurrentUser,
} from "./useCurrentUser";
import { filterArrayDuplicates } from "../../app/utils/filterArrayDuplicates";
import { useOnboardingGuard } from "./useOnboardingGuard";
import { useTranslation } from "react-i18next";
import { apiClient, type Api } from "../../../services/api";

type VideoListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Video" }
>;

export function useLikeVideo(
  video: VideoListItem | null,
  analyticsListName: AnalyticsItemList
) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [isReady, setReady] = useState(false);
  const likesIds = currentUser === null ? [] : currentUser.likedContent;
  const isLikedStore = video ? likesIds.includes(video.id) : false;
  const [isLikedOptimistic, setLikedOptimistic] = useState(isLikedStore);
  const [isFetching, setFetching] = useState(false);
  const onboardingGuard = useOnboardingGuard();

  // Setup hook and state once video is set. Keyed on the id, not the object:
  // a refetch hands over a new object for the same video, and resetting then
  // would overwrite a like in flight, and the next effect would undo it.
  useEffect(() => {
    if (video) {
      setLikedOptimistic(isLikedStore);
      setReady(true);
    }
  }, [video === null ? null : video.id]);

  useEffect(() => {
    if (
      isReady &&
      !isFetching &&
      isLikedOptimistic !== isLikedStore &&
      video !== null &&
      currentUser !== null
    ) {
      updateStatus(video, currentUser);
    }
  }, [isLikedOptimistic, isLikedStore]);

  async function toggleLike(video: VideoListItem) {
    try {
      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to like this content. ")
        )
      ) {
        return;
      }

      setLikedOptimistic(!isLikedOptimistic);
      iosGenerateHaptic(HapticEffect.SUCCESS);
      displayToast(
        isLikedOptimistic
          ? t("Removed from your liked videos")
          : t("Video liked. Added to your profile."),
        "success"
      );

      if (!isLikedOptimistic) {
        iosPlaySound(SoundEffect.LIKE);
        gaEventVideoLike({
          video,
          listName: analyticsListName,
        });
        gaEventContentLike(
          convertVideoToItemData({ video, listName: analyticsListName })
        );
      }
    } catch (error) {
      console.error("Couldn't like/unlike video.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function updateStatus(video: VideoListItem, currentUser: CurrentUser) {
    try {
      const videoId = video.id;
      setFetching(true);

      if (isLikedOptimistic) {
        await apiClient.post("/webapp/private/content/:id/likes", {
          param: { id: videoId },
        });
      } else {
        await apiClient.delete("/webapp/private/content/:id/likes", {
          param: { id: videoId },
        });
      }

      let likedContent = currentUser.likedContent;
      if (isLikedOptimistic) {
        likedContent = filterArrayDuplicates([
          ...currentUser.likedContent,
          videoId,
        ]);
      } else {
        likedContent = currentUser.likedContent.filter((id) => id !== videoId);
      }
      replaceCurrentUserData({ ...currentUser, likedContent });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setFetching(false);
    }
  }

  return {
    isLiked: isLikedOptimistic,
    toggleLike,
  };
}
